<template>
  <div class="modal-backdrop" @click.self="close">
    <div class="modal-content">
      <h2 class="modal-header">Card Holder Details</h2>

      <div class="card-holder-wrapper">
        <div class="details-left">
          <div class="detail-row">
            <span class="detail-label">Payor:</span>
            <span class="detail-value">{{ member.payor }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Insured:</span>
            <span class="detail-value">{{ member.name }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Company:</span>
            <span class="detail-value">{{ member.company }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Main Company:</span>
            <span class="detail-value">{{ member.mainCompany }}</span>
          </div>
        </div>

        <div class="details-right">
          <div class="detail-row">
            <span class="detail-label">IWC Card No.:</span>
            <span class="detail-value">{{ member.cardNum }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Birthdate:</span>
            <span class="detail-value">{{ member.birthdate }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Payment Count:</span>
            <span class="detail-value">{{ payments.length }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Payment Method:</span>
            <span class="detail-value">{{ member.paymentMethod }}</span>
          </div>
        </div>
      </div>

      <h2 class="modal-header">Payment Details</h2>

      <div class="payment-table-wrapper">
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <!-- Desktop table -->
        <table class="payment-table" v-if="!isMobile && !loading">
          <thead>
            <tr>
              <th>Payment Period</th>
              <th>Posted Premium</th>
              <th>Date Collected</th>
              <th>Date Posted</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(payment, index) in payments" :key="index">
              <td>{{ formatDatePayments(payment.paymentPeriod) }}</td>
              <td>{{ Number(payment.premiumRate).toFixed(2) }}</td>
              <td>{{ formatDatePayments(payment.collectionDate) }}</td>
              <td>{{ formatDateTimeLong(payment.processedAt) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Mobile card version -->
        <div class="mobile-payment-cards" v-if="isMobile && !loading">
          <div
            class="payment-card"
            v-for="(payment, index) in payments"
            :key="index"
          >
            <div class="payment-card-row">
              <span class="payment-card-label">Payment Period:</span>
              <span class="payment-card-value">{{
                formatDatePayments(payment.paymentPeriod)
              }}</span>
            </div>
            <div class="payment-card-row">
              <span class="payment-card-label">Posted Premium:</span>
              <span class="payment-card-value"
                >{{ Number(payment.premiumRate).toFixed(2) }}</span
              >
            </div>
            <div class="payment-card-row">
              <span class="payment-card-label">Date Collected:</span>
              <span class="payment-card-value">{{
                formatDatePayments(payment.collectionDate)
              }}</span>
            </div>
            <div class="payment-card-row">
              <span class="payment-card-label">Date Posted:</span>
              <span class="payment-card-value">{{
                formatDateTimeLong(payment.processedAt)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <button :class="['close-btn', route.path.startsWith('/iwc') ? 'close-btn-iwc' : 'close-btn-default']" @click="close">Close</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import axios from "axios";
import moment from "moment-timezone";
import { useRoute } from "vue-router";

export default {
  props: {
    member: Object,
    show: Boolean,
  },
  emits: ["close"],
  setup(props, { emit }) {
    const payments = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const isMobile = ref(false);
    const route = useRoute();

    const handleResize = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    const fetchPayments = async () => {
      if (!props.member?.id) return;
      loading.value = true;
      error.value = null;

      const endpoint = route.path.startsWith("/iwc")
        ? "/iwcpayment-list"
        : "/payment-list";

      try {
        const res = await axios.get(endpoint, {
          params: { planholderId: props.member.id },
        });

        payments.value = res.data.map((p) => ({
          paymentPeriod: p.paymentPeriod || p.payment_period || "N/A",
          premiumRate: p.premiumRate || p.premium || "N/A",
          collectionDate: p.collectionDate || p.dtreceived || "N/A",
          processedAt: p.processedAt || p.processdate || "N/A",
        }));
      } catch (err) {
        error.value = "Failed to fetch payment records.";
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const close = () => {
      emit("close");
    };

    onMounted(() => {
      fetchPayments();
      handleResize();
      window.addEventListener("resize", handleResize);
    });

    const formatDateShort = (isoDateString) => {
      if (!isoDateString) return "N/A";
      return moment.utc(isoDateString).tz("Asia/Manila").format("MM/DD/YYYY");
    };

    const formatDateTimeLong = (isoDateString) => {
      if (!isoDateString) return "N/A";
      if (route.path.startsWith("/iwc")) {
        return isoDateString;
      }
      return moment
        .utc(isoDateString)
        .tz("Asia/Manila")
        .format("MMMM D, YYYY h:mm A");
    };

    const formatDatePayments = (isoDateString) => {
      if (!isoDateString) return "N/A";
      if (route.path.startsWith("/iwc")) {
        return isoDateString;
      }
      return moment.utc(isoDateString).tz("Asia/Manila").format("MMMM D, YYYY");
    };

    return {
      payments,
      close,
      loading,
      error,
      formatDateShort,
      formatDateTimeLong,
      formatDatePayments,
      isMobile,
      route,
    };
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  width: 70%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  font-size: 20px;
  font-weight: 700;
  margin-top: 10px;
  margin-bottom: 12px;
  color: #333;
}

.card-holder-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 60px;
  margin-bottom: 35px;
}

.details-left,
.details-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 17px;
  border-bottom: 1px solid white;
  padding: 4px 0;
}

.detail-label {
  font-weight: 600;
  color: #333;
  min-width: 160px;
}

.detail-value {
  color: #444;
  text-align: left;
  flex: 1;
}

.payment-table-wrapper {
  max-height: 303px;
  overflow-y: auto;
  border: 1px solid #dce0e5;
  border-radius: 6px;
}

.payment-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.payment-table th,
.payment-table td {
  padding: 8px 10px;
  font-size: 17px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.payment-table th {
  background-color: #dce0e5;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.payment-table tbody tr:nth-child(odd) {
  background-color: #f9fafc;
}

.payment-table tbody tr:nth-child(even) {
  background-color: white;
}

.close-btn {
  background-color: #f29900;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 20px;
}

.close-btn:hover {
  background-color: #d97706;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 4px solid #ccc;
  border-top-color: #007bff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.mobile-payment-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-card {
  background-color: #f9fafc;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-size: 15px;
}

.mobile-payment-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-card {
  background-color: #f9fafc;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  font-size: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.payment-card-label {
  font-weight: 600;
  color: #333;
  flex: 1;
}

.payment-card-value {
  flex: 1;
  text-align: right;
  color: #444;
  white-space: nowrap;
}

.close-btn {
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 20px;
}

.close-btn-default {
  background-color: #f29900;
}

.close-btn-default:hover {
  background-color: #d97706;
}

.close-btn-iwc {
  background-color: #3f9c35;
}

.close-btn-iwc:hover {
  background-color: #34732d;
}


@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    padding: 20px;
  }

  .card-holder-wrapper {
    flex-direction: column;
    gap: 24px;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-label {
    min-width: auto;
    font-size: 15px;
  }

  .detail-value {
    font-size: 15px;
    width: 100%;
  }

  .payment-table-wrapper {
    overflow-x: auto;
    max-height: 503px;
  }

  .payment-table {
    min-width: 600px;
  }

  .modal-header {
    font-size: 18px;
  }

  .close-btn {
    width: 100%;
    font-size: 16px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    padding: 20px;
  }

  .card-holder-wrapper {
    flex-direction: column;
    gap: 24px;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-label {
    min-width: auto;
    font-size: 15px;
  }

  .detail-value {
    font-size: 15px;
    width: 100%;
  }

  .payment-table {
    min-width: 600px;
  }

  .modal-header {
    font-size: 18px;
  }

  .close-btn {
    width: 100%;
    font-size: 16px;
  }
}
</style>
